import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plane, Paperclip, Download, Upload } from "lucide-react";
import { toast } from "sonner";

const EMPTY_LEG = { date: "", time: "", flight_number: "", notes: "" };
const emptyTransport = () => ({
  needed: true, rate_amount: 0, rate_currency: "KES", contact_phone: "",
  pickup_location: "", dropoff_location: "", notes: "",
  inbound: { ...EMPTY_LEG }, outbound: { ...EMPTY_LEG },
});

export function AgencyTransportDialog({ booking, marginDays, onSaved, onClose }) {
  const [saving, setSaving] = useState(false);
  const [stayFrom, setStayFrom] = useState("");
  const [stayTo, setStayTo] = useState("");
  const [tr, setTr] = useState(emptyTransport());
  const [itineraries, setItineraries] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!booking) return;
    setStayFrom(booking.stay_from);
    setStayTo(booking.stay_to);
    const t = booking.transport;
    setTr(t ? {
      ...emptyTransport(), ...t,
      inbound: { ...EMPTY_LEG, ...(t.inbound || {}) },
      outbound: { ...EMPTY_LEG, ...(t.outbound || {}) },
    } : emptyTransport());
    api.get(`/documents/entity/booking/${booking.id}`)
      .then(({ data }) => setItineraries((data || []).filter((d) => d.requirement === "Itinerary")))
      .catch(() => setItineraries([]));
  }, [booking]);

  const uploadItinerary = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", "booking");
      fd.append("entity_id", booking.id);
      fd.append("requirement", "Itinerary");
      fd.append("file", file);
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItineraries((prev) => [data, ...prev]);
      toast.success("Itinerary uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const downloadItinerary = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  if (!booking) return null;
  const leg = (which, patch) => setTr((p) => ({ ...p, [which]: { ...p[which], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/bookings/${booking.id}/transport`, {
        transport: tr, stay_from: stayFrom, stay_to: stayTo,
      });
      toast.success("Changes saved — the hospitality team has been notified");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save changes");
    } finally { setSaving(false); }
  };

  const LegFields = (which, label) => (
    <div className="rounded-lg border border-[#E8E8E6] p-3 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#1B4332]">{label}</p>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1"><Label className="text-xs">Date</Label>
          <Input type="date" className="h-9" value={tr[which].date}
                 onChange={(e) => leg(which, { date: e.target.value })} data-testid={`atd-${which}-date`} /></div>
        <div className="space-y-1"><Label className="text-xs">Time</Label>
          <Input type="time" className="h-9" value={tr[which].time}
                 onChange={(e) => leg(which, { time: e.target.value })} data-testid={`atd-${which}-time`} /></div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Flight number</Label>
        <Input className="h-9" placeholder="e.g. KQ101" value={tr[which].flight_number}
               onChange={(e) => leg(which, { flight_number: e.target.value })} data-testid={`atd-${which}-flight`} /></div>
      <div className="space-y-1"><Label className="text-xs">Notes</Label>
        <Input className="h-9" value={tr[which].notes}
               onChange={(e) => leg(which, { notes: e.target.value })} data-testid={`atd-${which}-notes`} /></div>
    </div>
  );

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="agency-transport-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-[#1B4332]" /> Flight &amp; transport — {booking.visitor_name}
          </DialogTitle>
          <DialogDescription>
            Update flight details and shift the stay dates by up to {marginDays} day{marginDays === 1 ? "" : "s"}.
            The hospitality team will be notified and will acknowledge your change.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <p className="text-xs font-semibold text-amber-900 mb-2">
              Stay dates <span className="font-normal">(adjustable by ±{marginDays} day{marginDays === 1 ? "" : "s"})</span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">Arrival</Label>
                <Input type="date" className="h-9 bg-white" value={stayFrom}
                       onChange={(e) => setStayFrom(e.target.value)} data-testid="atd-stay-from" /></div>
              <div className="space-y-1"><Label className="text-xs">Departure</Label>
                <Input type="date" className="h-9 bg-white" value={stayTo}
                       onChange={(e) => setStayTo(e.target.value)} data-testid="atd-stay-to" /></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {LegFields("inbound", "Arrival flight")}
            {LegFields("outbound", "Departure flight")}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">Pickup location</Label>
              <Input className="h-9" value={tr.pickup_location}
                     onChange={(e) => setTr((p) => ({ ...p, pickup_location: e.target.value }))} data-testid="atd-pickup" /></div>
            <div className="space-y-1"><Label className="text-xs">Drop-off location</Label>
              <Input className="h-9" value={tr.dropoff_location}
                     onChange={(e) => setTr((p) => ({ ...p, dropoff_location: e.target.value }))} data-testid="atd-dropoff" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Contact phone (traveller)</Label>
            <Input className="h-9" value={tr.contact_phone}
                   onChange={(e) => setTr((p) => ({ ...p, contact_phone: e.target.value }))} data-testid="atd-phone" /></div>
          <div className="space-y-1"><Label className="text-xs">Transport notes</Label>
            <Textarea rows={2} value={tr.notes}
                      onChange={(e) => setTr((p) => ({ ...p, notes: e.target.value }))} data-testid="atd-notes" /></div>

          {/* Itineraries — upload & download */}
          <div className="rounded-lg border border-[#E8E8E6] p-3 space-y-2" data-testid="atd-itineraries">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#1B4332] flex items-center gap-1">
              <Paperclip className="h-3.5 w-3.5" /> Itineraries
            </p>
            {itineraries.length > 0 ? (
              <ul className="space-y-1">
                {itineraries.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 text-xs" data-testid={`itinerary-row-${d.id}`}>
                    <button type="button" onClick={() => downloadItinerary(d)}
                            className="shrink-0 text-[#40916C] hover:text-[#1B4332]" title={`Download ${d.filename}`}
                            data-testid={`itinerary-download-${d.id}`}>
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    <span className="truncate flex-1" title={d.filename}>{d.filename}</span>
                    <span className="text-[#7A8A84] shrink-0">{d.uploaded_by_name}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-[#7A8A84] italic">No itinerary uploaded yet.</p>
            )}
            <label className="flex items-center gap-2 text-xs text-[#525C58] cursor-pointer w-fit">
              <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-[#1B4332]/20 text-[#1B4332] hover:bg-[#1B4332] hover:text-white transition-colors">
                <Upload className="h-3 w-3" /> {uploading ? "Uploading…" : "Upload itinerary"}
              </span>
              <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                     disabled={uploading}
                     onChange={(e) => { uploadItinerary(e.target.files[0]); e.target.value = ""; }}
                     data-testid="itinerary-upload-input" />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="atd-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="atd-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
